import { useEffect, useRef, useState } from 'react';

import { Button, Grid, Tooltip, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Box } from '@mui/material';
import { format } from 'date-fns';
import { useQuery } from 'react-query';
import { getCriteriaEntry, getRankingEntry } from 'src/services/ranking';
import { getFiagroRankings } from 'src/services/ranking/fiagroRanking';
import { useDebounce } from 'src/utils/hooks';

import { useAnalyticalStyles } from './styles';
import { CustomSwitch } from '../../../components/CustomSwitch';
import { If } from '../../../components/If';
import RankingFilters from '../../../components/RankingFilters';
import { useAuthContext } from '../../../context/AuthContextProvider';
import { findRanking } from '../../../services/ranking';
import { truncateText } from '../../../utils/string';
import { Statistics } from '../Statistics';
import { StatisticsResume } from '../StatisticsResume';
import { TableRanking } from '../TableRanking';

const numberCriterias = [
  'operations',
  'negotiatedefundnumber',
  'tradesnumber',
  'titlesnumber',
  'operationsissued',
  'trades',
  'negotiatedefundnumber',
  'titlesnumberclassified',
  'fundnumber',
];

const percentageCriterias = [
  'pricevariance',
  'dividendyield',
  'effectivereturn',
  'marketcapitalization',
];

const PAGE_SIZE = 20;

export default function AnalyticalRanking({
  state,
  setState,
  hasConsolidated,
  markets,
  rankings,
  filters,
  allFilters,
  methodology,
  handlePrint,
  loadingFilters,
}) {
  const classes = useAnalyticalStyles();
  const { auth } = useAuthContext();
  const [isReadMore, setIsReadMore] = useState(false);
  const [showStatisticResume, setShowStatisticResume] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState('position');
  const [sortOrder, setSortOrder] = useState('asc');
  // Começa true: bloqueia a query até o RankingFilters terminar de inicializar
  const [isFiltersLoading, setIsFiltersLoading] = useState(true);
  const scrollRef = useRef(null);
  const hasPermission =
    auth?.user?.subscription?.plan?.permissions['rankings_completo'];

  const debouncedSearch = useDebounce(searchName, 500);

  const isFiagroMarket = state.market === 'FIAGRO';

  // Só dispara a query quando todos os parâmetros obrigatórios estão presentes
  // e os filtros terminaram de carregar (previne query com params inválidos da URL)
  const hasAllRequiredParams = !!(
    state.market &&
    state.ranking &&
    state.criteria &&
    state.year &&
    state.timeRange
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'rankings',
      state,
      filters,
      page,
      debouncedSearch,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      isFiagroMarket
        ? getFiagroRankings({
            type: state.ranking,
            criteria: state.criteria,
            year: state.year,
            timeRange: state.timeRange,
            keyword: debouncedSearch,
            page: page || 1,
            pageSize: PAGE_SIZE,
            sortBy,
            sortOrder,
            isLoggedIn: auth.isLoggedIn,
          })
        : findRanking({
            filters: {
              market: state.market.toLowerCase(),
              year: state.year,
              type: state.ranking,
              criteria: state.criteria,
              timeRange: state.timeRange,
              keyword: debouncedSearch,
            },
            isLoggedIn: auth.isLoggedIn,
            page: page || 1,
            pageSize: PAGE_SIZE,
            sortBy,
            sortOrder,
          }),
    onSuccess(data) {
      setPage(parseInt(data.page, 10));
      setPageCount(parseInt(data.pageCount, 10));
    },
    enabled: !loadingFilters && !isFiltersLoading && hasAllRequiredParams,
  });

  const criteriaName =
    filters &&
    getCriteriaEntry(
      getRankingEntry(filters, state?.market, state.ranking),
      state.criteria,
    )?.name;

  const scrollSmoothToElement = (el) => {
    window.scrollTo({
      behavior: 'smooth',
      top: window.scrollY + (el?.getBoundingClientRect().top ?? 0),
    });
  };

  const handleChangePage = (value) => {
    const newValue = parseInt(value, 10);
    if (!isNaN(newValue)) {
      setPage(newValue);
    }

    scrollSmoothToElement(scrollRef.current);
  };

  const handleSortChange = (column, direction) => {
    setSortBy(column);
    setSortOrder(direction);
    setPage(1);
  };

  useEffect(() => {
    refetch();
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    setSortBy('position');
    setSortOrder('asc');
    setPage(1);
  }, [
    state.market,
    state.ranking,
    state.criteria,
    state.year,
    state.timeRange,
  ]);

  return (
    <Box id="rankings-analytical" py={4} px={1} ref={scrollRef}>
      <Grid container>
        <Grid item xs={12} md={9}>
          <RankingFilters
            state={state}
            setState={setState}
            classes={classes}
            rankings={rankings}
            filters={allFilters}
            hasConsolidated={hasConsolidated}
            isLinked={!!state}
            hasYear
            hasCriteria
            typeRank="ranking_general"
            search={searchName}
            setSearch={setSearchName}
            onLoadingChange={setIsFiltersLoading}
          />
        </Grid>
        <Grid item xs={12} md={3} className={classes.gridDate}>
          <Typography className="updated">
            {data && `Atualizado em ${data?.updatedAt}`}
          </Typography>
          <Typography className="baseMonth">
            {data &&
              `Mês base ${format(
                new Date(data?.year, data?.month - 1),
                'MM/yyyy',
              )}`}
          </Typography>
        </Grid>
      </Grid>
      <Grid container style={{ marginTop: 15 }}>
        <Grid item xs={12} sm={9} container>
          <Typography color="secondary" className={classes.title}>
            Ranking Uqbar
          </Typography>
          <Typography className={classes.title}>
            {isLoading ? <Skeleton width={200} /> : data?.label}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={3} className={classes.gridSwitch}>
          <Tooltip
            disableFocusListener
            disableTouchListener={hasPermission}
            disableHoverListener={hasPermission}
            arrow
            title={
              'É necessário obter um plano para utilizar essa funcionalidade'
            }
            placement="top"
            classes={{ tooltip: classes.tooltip }}>
            <Box className={classes.resumeSwitch}>
              <CustomSwitch
                color="secondary"
                checked={state.resume}
                onChange={(e) => {
                  setShowStatisticResume(e.target.checked);
                  setShowStatistics(false);
                }}
                inputProps={{ 'aria-label': 'trocar tipo de exibição' }}
                disabled={!hasPermission || !data}
              />
              <Typography variant="caption" style={{ color: '#6D6E71' }}>
                Resumo
              </Typography>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      <Box>
        {isReadMore ? (
          <Typography
            className={classes.textCriteria}
            dangerouslySetInnerHTML={{
              __html: methodology,
            }}
          />
        ) : (
          <Typography
            className={classes.textCriteria}
            dangerouslySetInnerHTML={{
              __html: truncateText(methodology, 50),
            }}
          />
        )}
        <If condition={methodology?.length > 50}>
          <Button
            variant="text"
            color="secondary"
            onClick={() => setIsReadMore((prev) => !prev)}
            className={classes.readMoreButton}>
            {isReadMore ? 'Leia menos' : 'Leia mais'}
          </Button>
        </If>
      </Box>

      {!isLoading && showStatisticResume && (
        <StatisticsResume
          data={data?.ranks}
          setShowStatistics={setShowStatistics}
          showStatistics={showStatistics}
          state={state}
          numberCriterias={numberCriterias}
          valueLabel={data?.valueLabel}
          hasConsolidated={hasConsolidated}
        />
      )}

      {showStatistics ? (
        <Statistics
          state={state}
          numberCriterias={numberCriterias}
          valueLabel={data?.valueLabel}
          loading={isLoading}
        />
      ) : (
        <TableRanking
          loading={isLoading || isFiltersLoading}
          rank={data?.ranks}
          valueLabel={data?.valueLabel}
          rankingLabel={data?.label}
          state={state}
          hasConsolidated={hasConsolidated}
          month={data?.month}
          searchName={searchName}
          criteriaLabel={criteriaName}
          numberCriterias={numberCriterias}
          percentageCriterias={percentageCriterias}
          handlePrint={handlePrint}
          page={page}
          pageCount={pageCount}
          handleChangePage={handleChangePage}
          hideDetails={isFiagroMarket}
          onSortChange={handleSortChange}
        />
      )}
    </Box>
  );
}
